import React, { useCallback, useEffect, useState } from 'react';
import {
  Eye, Lightbulb, SkipForward, Shuffle, Trophy, Timer, Play, Pause, ChevronsRight,
} from 'lucide-react';
import { EMOJI_PUZZLES, CATEGORY_COLORS } from '../../data/emojiPuzzles';
import { TEAM_META, TEAM_IDS, shuffle } from '../../data/teams';
import GameOver from './GameOver';

// Each team gets a full minute on the clock for a puzzle.
const ROUND_SECONDS = 60;

const otherTeam = (id) => (id === 'vikings' ? 'panthers' : 'vikings');

// Puzzle 1 opens with the left-hand team; after that the first go alternates so
// neither side always gets the first crack at a fresh riddle.
const starterFor = (index) => (index % 2 === 0 ? TEAM_IDS[0] : TEAM_IDS[1]);

const clock = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

export default function EmojiGuess({ scores, onAward, onResetScores }) {
  const [deck, setDeck] = useState(() => shuffle(EMOJI_PUZZLES));
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [showHint, setShowHint] = useState(false);

  // ── Turn state ─────────────────────────────────────────────────────────────
  // One puzzle = up to two turns of ROUND_SECONDS, one per team. The host runs
  // the clock: it never starts on its own, so there is always a beat to read the
  // riddle out and settle the room before a team is on the spot.
  const [activeTeam, setActiveTeam] = useState(() => starterFor(0));
  const [running, setRunning] = useState(false);
  const [seconds, setSeconds] = useState(ROUND_SECONDS);
  const [turnsDone, setTurnsDone] = useState([]);

  const finished = index >= deck.length;
  const current = deck[index];
  const bothTurnsDone = turnsDone.length >= TEAM_IDS.length;

  // Fresh turn state on every new puzzle.
  useEffect(() => {
    setActiveTeam(starterFor(index));
    setRunning(false);
    setSeconds(ROUND_SECONDS);
    setTurnsDone([]);
  }, [index]);

  // Bank the current team's turn and hand over — or, if both have had their
  // minute, give up and show the answer. The handover deliberately leaves the
  // clock PAUSED so the host decides when the next team is under pressure.
  const endTurn = useCallback(() => {
    setRunning(false);
    const done = turnsDone.includes(activeTeam) ? turnsDone : [...turnsDone, activeTeam];
    setTurnsDone(done);
    const next = otherTeam(activeTeam);
    if (done.includes(next)) {
      setRevealed(true);
    } else {
      setActiveTeam(next);
      setSeconds(ROUND_SECONDS);
    }
  }, [turnsDone, activeTeam]);

  // Tick down, then hand over on zero.
  useEffect(() => {
    if (!running || revealed || finished) return undefined;
    if (seconds > 0) {
      const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
      return () => clearTimeout(t);
    }
    endTurn();
    return undefined;
  }, [running, revealed, finished, seconds, endTurn]);

  const goNext = () => { setRevealed(false); setShowHint(false); setIndex((x) => x + 1); };
  const award = (teamId) => { onAward(teamId, 1); goNext(); };
  const restart = () => {
    setDeck(shuffle(EMOJI_PUZZLES));
    setIndex(0);
    setRevealed(false);
    setShowHint(false);
    onResetScores();
  };

  // ── Win screen ─────────────────────────────────────────────────────────────
  if (finished) return <GameOver scores={scores} onRestart={restart} />;

  // ── Active puzzle ──────────────────────────────────────────────────────────
  const catColor = CATEGORY_COLORS[current.category] || '#475569';
  const progress = Math.round((index / deck.length) * 100);
  const activeMeta = TEAM_META[activeTeam];
  const lowTime = seconds <= 10;

  return (
    <div className="space-y-4">
      {/* Controls row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm font-semibold text-slate-500">
          Puzzle <span className="text-slate-900">{index + 1}</span> of {deck.length}
        </div>
        <button
          onClick={restart}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border bg-white text-slate-600 border-slate-200 hover:bg-slate-50 transition"
          title="Reshuffle the deck and reset scores"
        >
          <Shuffle size={13} /> Restart
        </button>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, background: 'var(--accent)' }} />
      </div>

      {/* Turn + clock */}
      {!revealed && (
        <div
          className="rounded-2xl border-2 p-4 sm:p-5 text-center transition-colors"
          style={{ borderColor: activeMeta.color, background: activeMeta.soft }}
        >
          <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.25em] text-slate-500">
            {turnsDone.length === 0 ? 'On the clock' : 'Second chance'}
          </div>
          <div className="mt-1 font-display font-extrabold text-lg sm:text-2xl" style={{ color: activeMeta.color }}>
            {activeMeta.name}
          </div>

          <div
            className={`mt-2 font-mono font-extrabold tabular-nums leading-none ${lowTime && running ? 'text-red-600' : 'text-slate-900'}`}
            style={{ fontSize: 'clamp(2.75rem, 12vw, 4.5rem)' }}
            aria-live="off"
          >
            {clock(seconds)}
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
            <button
              onClick={() => setRunning((v) => !v)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white transition hover:opacity-90"
              style={{ background: activeMeta.color }}
            >
              {running ? <Pause size={16} /> : <Play size={16} />}
              {running ? 'Pause' : (seconds === ROUND_SECONDS ? 'Start turn' : 'Resume')}
            </button>
            <button
              onClick={endTurn}
              className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-sm font-semibold border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 transition"
              title={turnsDone.length === 0 ? 'End this turn and hand over' : 'End this turn and reveal the answer'}
            >
              <ChevronsRight size={15} />
              {turnsDone.length === 0 ? 'Hand over' : 'Give up'}
            </button>
          </div>

          {/* Who has already used their minute */}
          <div className="mt-3 flex items-center justify-center gap-3 text-[11px] font-semibold">
            {TEAM_IDS.map((id) => (
              <span
                key={id}
                className={turnsDone.includes(id) ? 'text-slate-400 line-through' : 'text-slate-500'}
                title={turnsDone.includes(id) ? 'Minute used' : 'Minute still to come'}
              >
                <Timer size={11} className="inline mr-1 -mt-0.5" />
                {TEAM_META[id].short}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Puzzle card */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sm:p-10 text-center">
        <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full" style={{ color: catColor, background: `${catColor}14` }}>
          {current.category}
        </span>

        <div className="my-6 leading-none select-none" style={{ fontSize: 'clamp(3.5rem, 18vw, 7rem)' }} aria-label="emoji puzzle">
          {current.emoji}
        </div>

        {showHint && !revealed && (
          <div className="text-sm text-slate-500 italic mb-4">💡 {current.hint}</div>
        )}

        {revealed ? (
          <div className="text-2xl sm:text-3xl font-extrabold font-display text-slate-900 mb-2 animate-fadeIn">
            {current.answer}
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-center gap-2">
            {!showHint && (
              <button
                onClick={() => setShowHint(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
              >
                <Lightbulb size={15} /> Hint
              </button>
            )}
            <button
              onClick={() => setRevealed(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white transition"
            >
              <Eye size={15} /> Reveal answer
            </button>
          </div>
        )}
      </div>

      {/* Award — always available, because a team can shout the answer at 0:12
          and the host should not have to wait out the clock to give the point. */}
      <div className="space-y-2">
        <div className="text-center text-xs font-semibold uppercase tracking-wider text-slate-400">
          {revealed && bothTurnsDone ? 'Both minutes used — who got it?' : 'Award the point'}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {TEAM_IDS.map((id) => (
            <button
              key={id}
              onClick={() => award(id)}
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold text-white transition hover:opacity-90"
              style={{ background: TEAM_META[id].color }}
            >
              <Trophy size={16} /> +1 {TEAM_META[id].name}
            </button>
          ))}
        </div>
        <button
          onClick={goNext}
          className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold border border-slate-200 text-slate-500 hover:bg-slate-50 transition"
        >
          <SkipForward size={15} /> {revealed ? 'No one — next puzzle' : 'Skip this one'}
        </button>
      </div>
    </div>
  );
}
